/**
 * Tabs Block - Interactivity API View Script
 *
 * Handles tab switching, keyboard navigation, and ARIA updates.
 */

import { store, getContext, getElement } from '@wordpress/interactivity';

/**
 * How long the pointer must rest on a tab before it activates, in ms.
 *
 * A fixed design token rather than an author setting: sweeping across the strip
 * would otherwise fire every tab in turn, and the value that stops that without
 * feeling sluggish is a judgement call, not a per-instance one.
 */
const HOVER_ACTIVATE_DELAY = 300;

/** Pending hover timer, keyed by container so several blocks can coexist. */
const hoverTimers = new WeakMap();

const cancelPendingHover = ( container ) => {
	const timer = hoverTimers.get( container );
	if ( timer ) {
		clearTimeout( timer );
		hoverTimers.delete( container );
	}
};

/**
 * Update ARIA attributes and panel visibility.
 */
/**
 * This block's own tabs and panels, never a nested block's.
 *
 * A Tabs block can sit inside a tab panel, and a descendant query would then
 * return the inner block's tabs and panels interleaved with this one's —
 * mis-indexing every lookup, so activating tab 1 could unhide an inner panel and
 * leave the real one hidden. `style.scss` already guards this with child
 * combinators; these queries have to do the same.
 */
const ownTabs = ( container ) => {
	const nav = container.querySelector( ':scope > .brandy-tabs__nav' );
	return nav ? nav.querySelectorAll( ':scope > .brandy-tabs__tab' ) : [];
};

const ownPanels = ( container ) =>
	container.querySelectorAll(
		':scope > .brandy-tabs__panels > .brandy-tabs__panel'
	);

/**
 * Point the Segmented style's sliding marker at the active tab.
 *
 * Only that style paints a marker, so this is a no-op everywhere else — the
 * properties are written but nothing reads them. Geometry comes from
 * `offsetLeft`/`offsetTop`, which are relative to the nav because the style
 * makes it the offset parent; that keeps the numbers correct whether the strip
 * is centred, stretched, or has wrapped onto a second row.
 *
 * The `has-indicator-ready` class is what enables the CSS transition, so the
 * first measurement lands silently instead of sliding in from the corner.
 */
const syncIndicator = ( container, activeIndex ) => {
	const nav = container.querySelector( ':scope > .brandy-tabs__nav' );
	if ( ! nav ) {
		return;
	}

	const tab = ownTabs( container )[ activeIndex ];
	if ( ! tab ) {
		return;
	}

	nav.style.setProperty( '--tab-indicator-x', `${ tab.offsetLeft }px` );
	nav.style.setProperty( '--tab-indicator-y', `${ tab.offsetTop }px` );
	nav.style.setProperty( '--tab-indicator-w', `${ tab.offsetWidth }px` );
	nav.style.setProperty( '--tab-indicator-h', `${ tab.offsetHeight }px` );

	if ( ! nav.classList.contains( 'has-indicator-ready' ) ) {
		// Next frame, so the first geometry paints before transitions apply.
		requestAnimationFrame( () =>
			nav.classList.add( 'has-indicator-ready' )
		);
	}
};

const updateTabsState = ( container, activeIndex, shouldFocus ) => {
	const tabs = ownTabs( container );
	const panels = ownPanels( container );
	/*
	 * In link mode the tabs are anchors, not tablist members. Writing
	 * `aria-selected` onto a link is meaningless, and the roving `tabindex="-1"`
	 * would drop every inactive link out of the keyboard tab order entirely.
	 *
	 * Read from the flag the server sets, not from the presence of a
	 * `role="tab"` somewhere inside — a nested block's tabs would answer for
	 * this one.
	 */
	const isLinkMode = container.dataset.linkMode === 'true';

	tabs.forEach( ( tab, i ) => {
		const isActive = i === activeIndex;
		tab.classList.toggle( 'is-active', isActive );
		if ( ! isLinkMode ) {
			tab.setAttribute( 'aria-selected', String( isActive ) );
			tab.setAttribute( 'tabindex', isActive ? '0' : '-1' );
		}
	} );

	panels.forEach( ( panel, i ) => {
		const isActive = i === activeIndex;
		panel.classList.toggle( 'is-active', isActive );
		if ( isActive ) {
			panel.removeAttribute( 'hidden' );
		} else {
			panel.setAttribute( 'hidden', '' );
		}
	} );

	if ( shouldFocus && tabs[ activeIndex ] ) {
		tabs[ activeIndex ].focus();
	}

	syncIndicator( container, activeIndex );
	observedContainers.set( container, activeIndex );
};

/*
 * Tab widths move with the viewport — a wrapping strip re-flows, and a stretched
 * one divides the row differently — so the marker has to be re-measured or it
 * ends up parked over the wrong tab. Tracked per container so several blocks on
 * one page each keep their own active index.
 */
const observedContainers = new Map();

const resyncObserved = () => {
	observedContainers.forEach( ( index, container ) => {
		if ( container.isConnected ) {
			syncIndicator( container, index );
		} else {
			observedContainers.delete( container );
		}
	} );
};

if ( typeof window !== 'undefined' ) {
	window.addEventListener( 'resize', resyncObserved, { passive: true } );
}

store( 'brandy/tabs', {
	actions: {
		selectTab( event ) {
			const ctx = getContext();
			const { ref } = getElement();
			const container = ref.closest( '.brandy-tabs' );
			if ( ! container ) {
				return;
			}

			const button = event.target.closest( '[data-tab-index]' );
			if ( ! button ) {
				return;
			}

			/*
			 * A deliberate selection outranks a hover that has not landed yet.
			 * Without this, tabbing away from a tab the pointer happens to be
			 * resting on lets the old timer fire ~300ms later and drag the panel
			 * back, overriding the keyboard user with no input from them.
			 */
			cancelPendingHover( container );

			const index = parseInt( button.dataset.tabIndex, 10 );
			if ( isNaN( index ) || index === ctx.activeTab ) {
				return;
			}

			ctx.activeTab = index;
			updateTabsState( container, index, false );
		},
		/**
		 * Activate after the pointer has rested on a tab, not the instant it
		 * crosses one — otherwise dragging across the strip fires every tab on
		 * the way past.
		 */
		hoverTab( event ) {
			const ctx = getContext();
			const { ref } = getElement();
			const container = ref.closest( '.brandy-tabs' );
			if ( ! container ) {
				return;
			}

			const tab = event.target.closest( '[data-tab-index]' );
			if ( ! tab ) {
				return;
			}

			const index = parseInt( tab.dataset.tabIndex, 10 );
			if ( isNaN( index ) || index === ctx.activeTab ) {
				return;
			}

			cancelPendingHover( container );
			hoverTimers.set(
				container,
				setTimeout( () => {
					hoverTimers.delete( container );
					ctx.activeTab = index;
					updateTabsState( container, index, false );
				}, HOVER_ACTIVATE_DELAY )
			);
		},
		/**
		 * Leaving before the delay elapses abandons the switch. The tab that was
		 * already showing stays put — reverting to some earlier one would make
		 * the panel flicker as the pointer travels.
		 */
		cancelHover() {
			const { ref } = getElement();
			const container = ref.closest( '.brandy-tabs' );
			if ( container ) {
				cancelPendingHover( container );
			}
		},
		handleKeydown( event ) {
			const ctx = getContext();
			const { ref } = getElement();
			const container = ref.closest( '.brandy-tabs' );
			if ( ! container ) {
				return;
			}
			const tabs = ownTabs( container );
			const count = tabs.length;
			if ( ! count ) {
				return;
			}

			let newIndex = ctx.activeTab;

			switch ( event.key ) {
				case 'ArrowRight':
				case 'ArrowDown':
					newIndex = ( ctx.activeTab + 1 ) % count;
					break;
				case 'ArrowLeft':
				case 'ArrowUp':
					newIndex =
						( ctx.activeTab - 1 + count ) % count;
					break;
				case 'Home':
					newIndex = 0;
					break;
				case 'End':
					newIndex = count - 1;
					break;
				default:
					return;
			}

			event.preventDefault();
			ctx.activeTab = newIndex;
			updateTabsState( container, newIndex, true );
		},
	},
	callbacks: {
		init() {
			const ctx = getContext();
			const { ref } = getElement();
			updateTabsState( ref, ctx.activeTab, false );
		},
	},
} );
